import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO, subMonths } from "date-fns"
import { Download, Loader2 } from "lucide-react"

import { SourceSpoilage, SpoilageByItemResponse, fetchSpoilageByItem } from "@/api/spoilage.tsx"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { downloadCsv } from "@/deduction_table/util.tsx"
import { SpoilageDisplayRow, SpoilageView, spoilage_csv, spoilage_rows, total_amount } from "@/spoilage/spoilage_display.ts"
import { exactCurrencyFormatter, formatDistributor, usePageTitle } from "@/utils"

const PERIODS: { months: number | null; label: string }[] = [
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "12 months" },
  { months: null, label: "All time" },
]

const VIEWS: { key: SpoilageView; label: string }[] = [
  { key: "item", label: "By item" },
  { key: "warehouse", label: "By warehouse" },
]

function ButtonGroup<T>({ options, value, onChange, className }: { options: { key: T; label: string }[]; value: T; onChange: (value: T) => void; className?: string }) {
  return (
    <div class={`flex gap-1 ${className ?? ""}`}>
      {options.map((option) => (
        <Button key={option.label} size="sm" variant={value === option.key ? "default" : "ghost"} onClick={() => onChange(option.key)}>
          {option.label}
        </Button>
      ))}
    </div>
  )
}

function ItemsTable({ rows, view, show_units }: { rows: SpoilageDisplayRow[]; view: SpoilageView; show_units: boolean }) {
  return (
    <div class="overflow-x-auto">
      <table class="w-full min-w-[820px] text-sm">
        <thead>
          <tr class="text-left text-slate-500 border-b">
            {view === "warehouse" && <th class="py-2 pr-4 font-medium">Warehouse</th>}
            <th class="py-2 pr-4 font-medium">Item</th>
            <th class="py-2 pr-4 font-medium text-right">Amount</th>
            <th class="py-2 pr-4 font-medium text-right">Share</th>
            {show_units && <th class="py-2 pr-4 font-medium text-right">Units</th>}
            <th class="py-2 pr-4 font-medium text-right">Deductions</th>
            <th class="py-2 font-medium">Last charged</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row: SpoilageDisplayRow) => (
            <tr key={row.key} class="border-b last:border-0 align-top">
              {view === "warehouse" && <td class="py-2 pr-4 whitespace-nowrap">{row.dc ?? "—"}</td>}
              <td class="py-2 pr-4">
                <div>{row.description ?? "—"}</div>
                <div class="text-slate-500 font-mono text-xs">{[row.upc && `UPC ${row.upc}`, row.item_number && `Item ${row.item_number}`].filter(Boolean).join(" · ")}</div>
              </td>
              <td class="py-2 pr-4 text-right font-medium">{exactCurrencyFormatter(row.amount)}</td>
              <td class="py-2 pr-4 text-right text-slate-600">{(row.share * 100).toFixed(1)}%</td>
              {show_units && <td class="py-2 pr-4 text-right">{row.units === null ? "—" : row.units.toLocaleString()}</td>}
              <td class="py-2 pr-4 text-right">{row.deduction_count.toLocaleString()}</td>
              <td class="py-2 whitespace-nowrap">{format(parseISO(row.last_invoice_date), "MMM d, yyyy")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SourceSection({ source, data, view }: { source: SourceSpoilage; data: SpoilageByItemResponse; view: SpoilageView }) {
  const distributor = formatDistributor(source.source)
  const written_off = spoilage_rows(data.items, source.source, false, view)
  const allowances = spoilage_rows(data.items, source.source, true, view)
  return (
    <Card>
      <CardHeader>
        <div class="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="text-base">{distributor}</CardTitle>
            <CardDescription>
              {exactCurrencyFormatter(source.total)} of spoilage across {source.deduction_count.toLocaleString()} deduction
              {source.deduction_count === 1 ? "" : "s"}.
              {source.not_itemised >= 0.01 && (
                <>
                  {" "}
                  {exactCurrencyFormatter(source.not_itemised)} of it is not broken out by item on the backup we hold
                  {source.deductions_without_items > 0 && <> ({source.deductions_without_items.toLocaleString()} with no item detail yet)</>}.
                </>
              )}
            </CardDescription>
          </div>
          {written_off.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => downloadCsv(spoilage_csv(written_off, distributor, view), `${source.source}_spoilage_by_item`)}>
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {written_off.length > 0 && (
          <div>
            <div class="text-sm font-medium mb-1">Product written off · {exactCurrencyFormatter(total_amount(written_off))}</div>
            <ItemsTable rows={written_off} view={view} show_units />
          </div>
        )}
        {written_off.length === 0 && allowances.length === 0 && (
          <div class="text-sm text-slate-500">No item detail on the backup for these deductions yet.</div>
        )}
        {allowances.length > 0 && (
          <div>
            <div class="text-sm font-medium">Spoilage allowances · {exactCurrencyFormatter(total_amount(allowances))}</div>
            <div class="text-sm text-slate-500 mb-1">
              A flat percentage of what stores bought, billed back by item. No product was written off to produce these.
            </div>
            <ItemsTable rows={allowances} view={view} show_units={false} />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Spoilage by item: which products each distributor wrote off, at which warehouse, for how much —
 * rolled up from the backup behind every spoilage deduction, so a brand can take it to the
 * distributor's buyer.
 */
export function SpoilagePage() {
  usePageTitle("Spoilage by item")
  const [months, setMonths] = useState<number | null>(12)
  const [view, setView] = useState<SpoilageView>("item")
  const start_date = months === null ? null : format(subMonths(new Date(), months), "yyyy-MM-dd")
  const { data, error, isLoading } = useSWR<SpoilageByItemResponse>(["spoilage-by-item", months], () => fetchSpoilageByItem(start_date), {
    revalidateOnFocus: false,
    keepPreviousData: true,
  })

  return (
    <div class="p-6 flex flex-col gap-6 max-w-7xl">
      <div>
        <h1 class="text-2xl font-semibold">Spoilage by item</h1>
        <p class="text-slate-600 mt-1 max-w-3xl">
          Which products each distributor wrote off, and at which warehouse, taken from the backup behind every spoilage deduction. Use it
          to show a distributor's buyer where they are over-ordering.
        </p>
      </div>
      <div class="flex items-center justify-between gap-4 flex-wrap">
        <ButtonGroup options={VIEWS} value={view} onChange={setView} />
        <ButtonGroup options={PERIODS.map((p) => ({ key: p.months, label: p.label }))} value={months} onChange={setMonths} />
      </div>
      {isLoading && !data ? (
        <div class="flex items-center gap-2 p-8 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading spoilage…
        </div>
      ) : error || !data ? (
        <div class="p-8 text-red-600">Could not load spoilage.</div>
      ) : data.sources.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-slate-500">No spoilage deductions in this period.</CardContent>
        </Card>
      ) : (
        data.sources.map((source: SourceSpoilage) => <SourceSection key={source.source} source={source} data={data} view={view} />)
      )}
    </div>
  )
}
